import React from 'react';

import { Box, Button, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { NavLinkInterface } from 'server/config-cache/types';

import NavBarLayout from '../NavBarLayout';
import UserMenu from '../UserMenu';

type Props = {
  accountLinks: NavLinkInterface[];
  ctaLink: NavLinkInterface;
  isAuthenticated: boolean;
  leftLinks: NavLinkInterface[];
  loginDisplayText: string;
  logout: () => Promise<void>;
  rightLinks: NavLinkInterface[];
};

const DesktopNavBar: React.FC<Props> = ({
  accountLinks,
  ctaLink,
  isAuthenticated,
  leftLinks,
  loginDisplayText,
  logout,
  rightLinks,
}) => {
  const router = useRouter();
  const theme = useTheme();

  const generateTabs = (links: NavLinkInterface[]) =>
    links.map((link) => {
      return (
        <Tab
          key={`${link.url}-${link.displayText}`}
          value={link.url}
          label={link.displayText}
          href={link.url}
          sx={[
            {
              color: theme.palette.grey[800],
              padding: '0 1.5rem',
            },
          ]}
        />
      );
    });

  const [desktopLeftLinks, desktopRightLinks] = [
    generateTabs(leftLinks),
    generateTabs(rightLinks),
  ];

  // `Tabs` component is unhappy if you prove it a value that's not represented as a `Tab`.
  //   You must give it a value of `false` if the current value isn't represented in the
  //   list of `Tab` components.
  const leftTabsValue =
    leftLinks.find((l) => l.url === router.pathname)?.url || false;

  const rightTabsValue =
    rightLinks.find((l) => l.url === router.pathname)?.url || false;

  return (
    <NavBarLayout
      leftComponent={
        <Tabs
          sx={{
            height: '100%',
            '.MuiTabs-flexContainer': {
              height: '100%',
            },
          }}
          value={leftTabsValue}
        >
          {desktopLeftLinks}
        </Tabs>
      }
      rightComponent={
        <>
          <Tabs
            value={rightTabsValue}
            sx={{
              height: '100%',
              '.MuiTabs-flexContainer': {
                height: '100%',
              },
            }}
          >
            {desktopRightLinks}
          </Tabs>

          {isAuthenticated ? (
            <UserMenu
              logout={logout}
              accountLinks={accountLinks}
              bottomLinks={[]}
              topLinks={[]}
            />
          ) : (
            <>
              <Box>
                <Button variant="text" href={'/login'}>
                  {loginDisplayText}
                </Button>
              </Box>
              <Box sx={{ marginLeft: '1.5rem' }}>
                <Button variant={'contained'} href={ctaLink.url}>
                  {ctaLink.displayText}
                </Button>
              </Box>
            </>
          )}
        </>
      }
    />
  );
};

export default DesktopNavBar;
